import React from 'react';
import { JsonLd } from 'react-schemaorg';
import map from 'lodash/map';
import get from 'lodash/get';
import { I18nHoc } from 'helpers/i18n';
import formatDate from 'helpers/formatDate';
import absoluteUrl from 'helpers/absoluteUrl';
import beautifyNumber from 'helpers/beautifyNumber';
import { AbstractObjectTile, TileView } from 'components/layouts';
import Link from 'components/link';
import { getToponyms } from 'components/layouts/abstract-object-tile';
import { iconTypes } from 'components/icon';

import {
  renderSignOfAvailabilityStock,
  renderSalesTitleAndStatuses, getAskDevHeader,
} from '../section-project/render-common';
import { checkSold } from '../section-project/cards-object/list-card';

import translates from '../section-projects/card-i18n';
import { ModalCallBack } from '../section-project/contact-to-developer';

@I18nHoc(translates())
export default class StockCard extends AbstractObjectTile {
  static displayName = 'StockCard';

  constructor(props, ctx) {
    super(props, ctx);
    this.imagesKey = 'bild';
    this.randNum = 0;
  }

  renderDevelopers(developers, bemClasses) {
    if (!developers || !developers.length) return null;

    return (
      <div {...bemClasses('developers')}>
        {map(developers, (item, key) =>
          <span key={key} {...bemClasses('developer')}>
            <span {...bemClasses('text', 'developer')} children={item.name_short} />
          </span>
        )}
      </div>
    );
  }

  renderImage(data, bemClasses) {
    const { i18n } = this;
    const image = get(data, 'title_image.id');
    const type = get(data, 'type') === 'living_complex' ? i18n('forApartments') : null;
    const header = get(data, 'display_name') || get(data, 'location_path');
    const locality = get(data, 'parent_display_path') ? `, ${get(data, 'parent_display_path')}` : null;
    const alt = `${i18n('stock')}${type} в ${header}${locality}` || '';
    const title = `${i18n('stock')} в ${data.display_name}` || null;
    const dataImg = { alt, title };
    const stocks_array = get(data, 'stocks_array') || [];

    if (!stocks_array.length) return null;

    this.randNum = Math.floor(Math.random() * stocks_array.length);
    const currentStock = get(stocks_array, `[${this.randNum}]`);

    if (!currentStock) return null;

    return (
      <>
        {super.renderImage(data, bemClasses, false, false, true, dataImg)}
        {renderSalesTitleAndStatuses.call(this, bemClasses, data, true)}
        {renderSignOfAvailabilityStock(bemClasses, currentStock)}
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            name: alt || null,
            description: title,
            contentLocation: get(data, 'parent_display_path', null),
            contentUrl: image ? absoluteUrl(`/images/thumb/full/${image}`) : null
          }}
        />
      </>
    );
  }

  setMinPrice = (bemClasses, minPrice={}) => {
    if (!minPrice) return null;
    const priceCurrencyName = minPrice.currency.id === 'uah' ?
      minPrice.currency.name_short :
      minPrice.currency.names_long;
    const priceClassDisplayName = minPrice.class.displayName || '';
    const priceCurrency = `${priceCurrencyName}${priceClassDisplayName}`;
    const priceBeginText = this.i18n('priceStocksBeginText');
    const priceMain = beautifyNumber(minPrice.value);
    let priceFullText = null;
    if (
      minPrice &&
      minPrice.value &&
      minPrice.currency &&
      minPrice.class
    ) {
      priceFullText = (
        <span {...bemClasses('text', 'price')} children={`${priceBeginText} ${priceMain} ${priceCurrency}`} />
      );
    }
    return priceFullText;
  }

  renderContent(data = {}, bemClasses) {
    const { i18n } = this;
    const {
      stocks_array, stocks_without_price, developers,
      all_flats_sold, all_commercial_sold, all_house_and_area_sold,
      all_flats_sold_object, $entity
    } = data;
    const stockItem = {};
    const stockItemSeo = {};
    const soldData = {
      all_flats_sold, all_commercial_sold, all_house_and_area_sold
    };
    const sold = checkSold.call(this, all_flats_sold_object, soldData, $entity);
    const currentStock = get(stocks_array, `[${this.randNum}]`);

    if (!currentStock) return null;

    if (currentStock){
      const from = currentStock.period_date_from ?
        ` ${i18n('stockFrom')} ${formatDate({ date: currentStock.period_date_from, format: 'dd.mm.yyyy' })}` : '';
      const to = currentStock.period_date_to ?
        ` ${i18n('stockTo')} ${formatDate({ date: currentStock.period_date_to, format: 'dd.mm.yyyy' })}` : '';

      stockItem.title = currentStock.title && <div {...bemClasses('description')}><span {...bemClasses('text', 'description')} children={currentStock.title} /></div>;
      stockItem.period = (from || to) && <div {...bemClasses('date')}><span {...bemClasses('text', 'date')} children={`${i18n('stock')}${from}${to}`} /></div>;

      stockItemSeo.from = get(currentStock, 'period_date_from');
      stockItemSeo.to = get(currentStock, 'period_date_to');
      stockItemSeo.title = `\ud83d\udd36 ${get(currentStock, 'title')}`;
      stockItemSeo.link = get(currentStock, 'link');
      stockItemSeo.discount = get(currentStock, 'discount');
    }
    stockItemSeo.image = get(data, 'title_image.id');
    stockItemSeo.displayName = get(data, 'display_name');
    // stockItemSeo.streetAddress = get(data, 'address');
    stockItemSeo.addressLocality = get(data, 'display_locality') || get(data, 'parent_display_path');
    stockItemSeo.geo = get(data, 'geo_centroid');
    stockItemSeo.price = get(data, 'min_price.value');
    stockItemSeo.priceCurrency = get(data, 'min_price.currency.name_standart');
    stockItemSeo.url = get(data, 'url');
    stockItemSeo.sold = get(data, 'sales_suspended_until') || sold.sold;
    const header = data.display_name || data.location_path;
    const minPrice = get(data, 'min_price');
    const priceFullText = stocks_without_price ? null : this.setMinPrice(bemClasses, minPrice);
    const askDevHeader = getAskDevHeader.call(this, bemClasses, data);
    const main_developer = get(data, 'main_developer[0]', {});
    const developerEmail = get(data, 'first_seller_parent_email');
    const withoutConversions = get(this, 'props.withoutConversions');
    const isDiscount = get(currentStock, 'is_discount');
    const modalBtnLabel = isDiscount ? i18n('aboutDiscount') : i18n('aboutDetail');
    const modalBtnIcon = isDiscount ? iconTypes.gift : iconTypes.phoneCalls;

    return (
      <>
        {header &&
          <div {...bemClasses('location')}>
            <span {...bemClasses('text')} children={header} />
          </div>
        }
        {this.renderDevelopers(developers, bemClasses)}
        {stockItem.title}
        {getToponyms.call(this, get(data, 'display_locality'), get(data, 'toponym'), {}, bemClasses)}
        {priceFullText}
        {stockItem.period}
        {developerEmail && !withoutConversions &&
          <div {...bemClasses('btnCallBack')}>
            <ModalCallBack
              classes={bemClasses}
              askDevHeader={askDevHeader}
              projectId={data.id}
              projectUrl={data.url}
              developer={main_developer}
              developerEmail={developerEmail}
              modalBtnProps={{
                label: modalBtnLabel,
                title: modalBtnLabel,
                inListDD: false,
                iconType: modalBtnIcon
              }}
            />
          </div>
        }
        {stockItemSeo &&
          <JsonLd
            item={{
              '@context': 'https://schema.org',
              '@type': 'SaleEvent',
              name: stockItemSeo.title,
              startDate: stockItemSeo.from,
              endDate: stockItemSeo.to,
              url: stockItemSeo.link,
              description: stockItemSeo.discount,
              image: stockItemSeo.image ? absoluteUrl(`/images/thumb/full/${stockItemSeo.image}`) : null,
              location: {
                '@type': 'Place',
                address: stockItemSeo.addressLocality ? {
                  '@type': 'PostalAddress',
                  // streetAddress: stockItemSeo.streetAddress,
                  addressLocality: stockItemSeo.addressLocality
                } : null,
                name: stockItemSeo.displayName,
                geo: stockItemSeo.geo && stockItemSeo.geo.type === 'Point' ? {
                  '@type': 'GeoCoordinates',
                  latitude: stockItemSeo.geo.coordinates[1],
                  longitude: stockItemSeo.geo.coordinates[0]
                } : null,
              },
              about: stockItemSeo.title,
              performer: stockItemSeo.displayName,
              organizer: 'Bild.ua',
              offers: stockItemSeo.price ? {
                '@type': 'Offer',
                price: stockItemSeo.price,
                priceCurrency: stockItemSeo.priceCurrency,
                availability: stockItemSeo.sold ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
                url: absoluteUrl(stockItemSeo.url),
                validFrom: stockItemSeo.from
              } : null
            }}
          />
        }
      </>
    );
  }

  renderSideBlock() {
    return null;
  }

  renderUnderneath(data = {}, bemClasses) {
    const { stocks_array, display_name, location_path, type } = data;
    const header = display_name || location_path;
    let dataType = 'other';
    if (type === 'living_complex') {
      dataType = 'zhk';
    }
    if (type === 'cottage_town') {
      dataType = 'cott';
    }
    if (type === 'company') {
      dataType = 'dev';
    }
    const place = get(this, 'props.params.place') || get(this, 'props.gtmProps.place');
    const stockItem = {};
    map(stocks_array, (it) => {
      stockItem.link = it.link && (
        <Link
          {...bemClasses('link', 'fill')}
          href={it.link}
          rel="nofollow"
          dataProps={{ 'target': header || 'other', 'promo': 'true', 'place': place || 'other', 'type': dataType }}
          external
          blank
          children={<span />}
        />
      );
    });

    return stockItem.link;
  }

  render() {
    const { object, dataMods, ...restProps } = this.props;

    return (
      <TileView
        {...restProps}
        data={object}
        dataMods={{ fill: false, bild: true, stock: true, ...dataMods }}
        onMouseOver={restProps.onMouseOver || this.handleHover}
        onMouseOut={restProps.onMouseOut || this.handleUnhover}
        linkCard={{ filledCard: false, filledImg: false }}
        renderImage={this.renderImage}
        renderContent={this.renderContent}
        renderSideBlock={this.renderSideBlock}
        renderUnderneath={this.renderUnderneath}
      />
    );
  }
}
